/**
 * Component Reference Resolver - Phase 2.1: Semantic Reference Resolution
 *
 * Resolves natural language references to actual component IDs with high accuracy.
 * Supports:
 * - Fuzzy name matching with Levenshtein distance
 * - Reference vocabulary with common aliases
 * - Semantic role matching (_semanticRole field)
 * - Positional references (above, below, first, right, left)
 * - Reference caching for performance
 *
 * Target: >85% accuracy for common references like "the header", "submit button"
 */

import { Component } from '@/types';
import { useEditorStore } from '@/stores/editor/editor-store';
import { logger } from '@/utils/logger';
import { templateInstantiationService } from './TemplateInstantiationService';
import type { BaseComponentProperties } from '@/types/component-properties';
import type {
  SemanticReference,
  ComponentMatch,
  ReferenceResolutionResult,
  ReferenceCacheEntry,
  ReferenceResolutionConfig,
  SemanticMatchType,
  ClarificationOption,
} from '@/types/ai';

// ============================================================================
// Configuration
// ============================================================================

const DEFAULT_CONFIG: ReferenceResolutionConfig = {
  confidenceThreshold: 0.7,
  maxFuzzyDistance: 3,
  enablePositionalReferences: true,
  enableSemanticRoles: true,
  enableAliases: true,
  cacheTTL: 60000, // 1 minute
  maxAlternatives: 5,
};

// ============================================================================
// Reference Vocabulary (Task 2.1.2)
// ============================================================================

/**
 * Comprehensive reference vocabulary mapping common terms to component patterns
 * Each entry maps a canonical term to its aliases
 */
const REFERENCE_VOCABULARY: Record<string, string[]> = {
  // Action buttons
  'submit': ['submit', 'save', 'send', 'confirm', 'ok', 'done', 'apply', 'create', 'add'],
  'cancel': ['cancel', 'close', 'dismiss', 'back', 'exit', 'discard', 'abort', 'nevermind'],
  'delete': ['delete', 'remove', 'trash', 'clear', 'erase', 'destroy'],
  'edit': ['edit', 'modify', 'change', 'update', 'revise'],

  // Navigation elements
  'header': ['header', 'title', 'heading', 'h1', 'h2', 'h3', 'page-title', 'main-title', 'top-bar'],
  'navigation': ['nav', 'menu', 'sidebar', 'navbar', 'navigation', 'sidenav', 'nav-menu', 'menubar'],
  'footer': ['footer', 'bottom', 'bottom-bar', 'page-footer'],
  'breadcrumb': ['breadcrumb', 'breadcrumbs', 'path', 'trail'],

  // Content sections
  'main': ['main', 'content', 'body', 'main-content', 'primary', 'center', 'main-area'],
  'sidebar': ['sidebar', 'side', 'aside', 'side-panel', 'left-panel', 'right-panel'],
  'card': ['card', 'tile', 'panel', 'box', 'container', 'widget'],
  'section': ['section', 'area', 'zone', 'region', 'block'],

  // Form elements
  'form': ['form', 'input-form', 'form-container', 'fields'],
  'input': ['input', 'field', 'textbox', 'text-field', 'entry'],
  'button': ['button', 'btn', 'action', 'cta', 'link-button'],
  'dropdown': ['dropdown', 'select', 'picker', 'combo', 'combobox'],
  'checkbox': ['checkbox', 'check', 'toggle', 'switch'],
  'label': ['label', 'caption', 'text-label', 'field-label'],

  // Data display
  'chart': ['chart', 'graph', 'visualization', 'viz', 'plot', 'diagram'],
  'table': ['table', 'grid', 'data-table', 'list', 'datagrid'],
  'gauge': ['gauge', 'meter', 'indicator', 'dial', 'speedometer'],
  'stats': ['stats', 'statistics', 'metrics', 'kpi', 'numbers', 'figures'],

  // Media
  'image': ['image', 'img', 'picture', 'photo', 'icon', 'graphic', 'logo'],
  'video': ['video', 'media', 'player', 'clip'],

  // Layout
  'container': ['container', 'wrapper', 'holder', 'parent', 'group'],
  'grid': ['grid', 'layout', 'columns', 'rows'],
  'flex': ['flex', 'flexbox', 'row', 'column', 'stack'],

  // Common UI patterns
  'modal': ['modal', 'dialog', 'popup', 'overlay', 'lightbox'],
  'alert': ['alert', 'notification', 'message', 'banner', 'toast'],
  'progress': ['progress', 'loader', 'spinner', 'loading', 'indicator'],
  'search': ['search', 'find', 'lookup', 'query', 'searchbox'],
  'filter': ['filter', 'filters', 'refine', 'sort'],
  'pagination': ['pagination', 'pager', 'pages', 'next-prev'],

  // Agricultural/IoT specific (for TerraMatrix)
  'sensor': ['sensor', 'reading', 'measurement', 'probe'],
  'tank': ['tank', 'vessel', 'container', 'reservoir', 'silo'],
  'field': ['field', 'zone', 'plot', 'area', 'section'],
  'equipment': ['equipment', 'device', 'machine', 'unit'],
  'status': ['status', 'state', 'condition', 'health'],
};

/**
 * Positional reference keywords
 */
const POSITIONAL_KEYWORDS = {
  above: { direction: 'vertical', comparator: 'less' as const },
  below: { direction: 'vertical', comparator: 'greater' as const },
  under: { direction: 'vertical', comparator: 'greater' as const },
  over: { direction: 'vertical', comparator: 'less' as const },
  left: { direction: 'horizontal', comparator: 'less' as const },
  right: { direction: 'horizontal', comparator: 'greater' as const },
  before: { direction: 'order', comparator: 'less' as const },
  after: { direction: 'order', comparator: 'greater' as const },
  first: { direction: 'index', index: 0 },
  second: { direction: 'index', index: 1 },
  third: { direction: 'index', index: 2 },
  last: { direction: 'index', index: -1 },
  top: { direction: 'vertical', comparator: 'less' as const },
  bottom: { direction: 'vertical', comparator: 'greater' as const },
};

function getFirstLowercaseString(
  properties: BaseComponentProperties,
  ...keys: string[]
): string {
  for (const key of keys) {
    const value = properties[key];
    if (typeof value === 'string') {
      return value.toLowerCase();
    }
  }

  return '';
}

// ============================================================================
// Legacy interface for backward compatibility
// ============================================================================

export interface ResolvedReference {
  componentId: string;
  component: Component;
  confidence: number;
  matchType: 'exact' | 'semantic' | 'contextual' | 'positional';
  semanticRole?: string;
}

// ============================================================================
// Component Reference Resolver Class
// ============================================================================

export class ComponentReferenceResolver {
  private static instance: ComponentReferenceResolver;
  private config: ReferenceResolutionConfig;

  // Reference cache (Task 2.1.6)
  private cache: Map<string, ReferenceCacheEntry> = new Map();
  private cacheInvalidationTimer: NodeJS.Timeout | null = null;

  // Legacy semantic mappings for backward compatibility
  private semanticMappings = {
    sidebar: ['sidebar', 'side-bar', 'aside', 'navigation-panel', 'nav-panel', 'side-panel', 'sidebar-container'],
    header: ['header', 'top-bar', 'navigation-header', 'page-header', 'nav-header', 'header-container'],
    footer: ['footer', 'bottom-bar', 'page-footer', 'footer-container'],
    navigation: ['navigation', 'nav', 'nav-menu', 'menu', 'navbar', 'nav-bar'],
    main: ['main', 'main-content', 'content', 'content-area', 'main-area', 'body', 'main-container', 'main-content-container'],
    title: ['title', 'heading', 'header-text', 'page-title', 'main-title', 'title-text'],
    description: ['description', 'subtitle', 'summary', 'content-description', 'intro'],
    button: ['button', 'btn', 'action-button', 'submit', 'cta', 'call-to-action'],
    left: ['left', 'left-side', 'left-panel', 'left-column'],
    right: ['right', 'right-side', 'right-panel', 'right-column'],
    center: ['center', 'middle', 'central', 'center-content'],
    top: ['top', 'upper', 'top-section'],
    bottom: ['bottom', 'lower', 'bottom-section'],
    cards: ['cards', 'card-grid', 'card-container', 'grid'],
    stats: ['stats', 'statistics', 'metrics', 'dashboard', 'stats-container'],
    form: ['form', 'input-form', 'form-container'],
    'sidebar-title': ['sidebar-title', 'navigation-title', 'nav-heading'],
    'content-title': ['content-title', 'main-heading', 'page-heading'],
    'logo': ['logo', 'brand', 'logo-container'],
  };

  private constructor(config: Partial<ReferenceResolutionConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    logger.debug("[ComponentReferenceResolver] Initialized with config", this.config);
  }

  static getInstance(config?: Partial<ReferenceResolutionConfig>): ComponentReferenceResolver {
    if (!ComponentReferenceResolver.instance) {
      ComponentReferenceResolver.instance = new ComponentReferenceResolver(config);
    }
    return ComponentReferenceResolver.instance;
  }

  // ============================================================================
  // Main Resolution Methods
  // ============================================================================

  /**
   * Enhanced reference resolution with semantic understanding (Task 2.1.5)
   * Primary entry point for Phase 2.1
   */
  resolveSemanticReference(
    referenceText: string,
    context?: {
      selectedComponents?: Component[];
      recentlyAddedComponents?: Component[];
      templateInstanceId?: string;
      pageId?: string;
    }
  ): ReferenceResolutionResult {
    const startTime = performance.now();
    const normalizedRef = this.normalizeReference(referenceText);
    const { components } = useEditorStore.getState();

    console.log(`[SemanticResolver] Resolving: "${referenceText}" (normalized: "${normalizedRef}")`);
    console.log(`[SemanticResolver] Available components: ${components.length}`);

    // Check cache first
    const cached = this.checkCache(normalizedRef, context?.pageId);
    if (cached) {
      const cachedComponent = components.find(c => c.id === cached.componentId);
      if (cachedComponent) {
        console.log(`[SemanticResolver] Cache HIT for "${normalizedRef}" -> ${cached.componentId}`);
        return {
          success: true,
          primaryMatch: {
            referenceText,
            resolvedComponentId: cached.componentId,
            confidence: cached.confidence,
            matchType: 'exact',
            resolutionPath: 'cache',
          },
          allMatches: [{
            id: cached.componentId,
            name: cachedComponent.name,
            type: cachedComponent.type,
            matchReason: 'Cached resolution',
            confidence: cached.confidence,
          }],
          needsClarification: false,
        };
      }
    }

    // Collect all potential matches with scores
    const allMatches: ComponentMatch[] = [];
    const confidenceScores = new Map<string, number>();

    // 1. Check for direct ID reference (highest priority)
    const directMatch = components.find(c => c.id === referenceText);
    if (directMatch) {
      console.log(`[SemanticResolver] Direct ID match: ${directMatch.id}`);
      this.updateCache(normalizedRef, directMatch.id, 1.0, context?.pageId);
      return this.createSuccessResult(referenceText, directMatch, 1.0, 'exact', 'Direct ID match');
    }

    // 2. Exact name match
    const exactNameMatch = components.find(c =>
      c.name?.toLowerCase() === normalizedRef
    );
    if (exactNameMatch) {
      console.log(`[SemanticResolver] Exact name match: ${exactNameMatch.name}`);
      this.updateCache(normalizedRef, exactNameMatch.id, 0.98, context?.pageId);
      return this.createSuccessResult(referenceText, exactNameMatch, 0.98, 'exact', 'Exact name match');
    }

    // 3. Template semantic references
    if (context?.templateInstanceId) {
      const templateComponent = templateInstantiationService.getComponentBySemanticReference(
        normalizedRef,
        context.templateInstanceId
      );
      if (templateComponent) {
        console.log(`[SemanticResolver] Template semantic match: ${templateComponent.id}`);
        return this.createSuccessResult(referenceText, templateComponent, 0.95, 'semantic', 'Template semantic role');
      }
    }

    // 4. Semantic role matching (Task 2.1.3)
    if (this.config.enableSemanticRoles) {
      const semanticMatches = this.matchBySemanticRole(normalizedRef, components);
      allMatches.push(...semanticMatches);
      semanticMatches.forEach(m => confidenceScores.set(m.id, Math.max(confidenceScores.get(m.id) || 0, m.confidence)));
    }

    // 5. Vocabulary alias matching (Task 2.1.2)
    if (this.config.enableAliases) {
      const aliasMatches = this.matchByVocabularyAlias(normalizedRef, components);
      allMatches.push(...aliasMatches);
      aliasMatches.forEach(m => confidenceScores.set(m.id, Math.max(confidenceScores.get(m.id) || 0, m.confidence)));
    }

    // 6. Fuzzy matching (Task 2.1.1)
    const fuzzyMatches = this.matchByFuzzyName(normalizedRef, components);
    allMatches.push(...fuzzyMatches);
    fuzzyMatches.forEach(m => confidenceScores.set(m.id, Math.max(confidenceScores.get(m.id) || 0, m.confidence)));

    // 7. Positional references (Task 2.1.4)
    if (this.config.enablePositionalReferences) {
      const positionalMatches = this.matchByPosition(normalizedRef, components, context);
      allMatches.push(...positionalMatches);
      positionalMatches.forEach(m => confidenceScores.set(m.id, Math.max(confidenceScores.get(m.id) || 0, m.confidence)));
    }

    // 8. Contextual references (selected/recent)
    if (context?.selectedComponents) {
      const contextualMatches = this.matchByContext(normalizedRef, context.selectedComponents, 'selected');
      allMatches.push(...contextualMatches);
      contextualMatches.forEach(m => confidenceScores.set(m.id, Math.max(confidenceScores.get(m.id) || 0, m.confidence)));
    }

    if (context?.recentlyAddedComponents) {
      const recentMatches = this.matchByContext(normalizedRef, context.recentlyAddedComponents, 'recent');
      allMatches.push(...recentMatches);
      recentMatches.forEach(m => confidenceScores.set(m.id, Math.max(confidenceScores.get(m.id) || 0, m.confidence)));
    }

    // Deduplicate and sort by confidence
    const uniqueMatches = this.deduplicateMatches(allMatches);
    const sortedMatches = uniqueMatches.sort((a, b) => b.confidence - a.confidence);

    const elapsed = performance.now() - startTime;
    console.log(`[SemanticResolver] Found ${sortedMatches.length} matches in ${elapsed.toFixed(2)}ms`);

    // Determine result
    if (sortedMatches.length === 0) {
      console.log(`[SemanticResolver] No matches found for "${referenceText}"`);
      return {
        success: false,
        allMatches: [],
        needsClarification: false,
      };
    }

    const topMatch = sortedMatches[0];

    // Single high-confidence match - success!
    if (topMatch.confidence >= this.config.confidenceThreshold) {
      // Check if there are close alternatives
      const closeAlternatives = sortedMatches.filter(
        m => m.id !== topMatch.id && m.confidence >= topMatch.confidence - 0.15
      );

      if (closeAlternatives.length > 0) {
        // Multiple close matches - need clarification
        console.log(`[SemanticResolver] Multiple close matches, requesting clarification`);
        return {
          success: false,
          primaryMatch: this.createSemanticReference(referenceText, topMatch),
          allMatches: sortedMatches.slice(0, this.config.maxAlternatives),
          needsClarification: true,
          clarificationOptions: this.createClarificationOptions(sortedMatches.slice(0, this.config.maxAlternatives)),
          confidenceScores,
        };
      }

      // Clear winner!
      console.log(`[SemanticResolver] SUCCESS: "${referenceText}" -> ${topMatch.id} (confidence: ${topMatch.confidence.toFixed(2)})`);
      const component = components.find(c => c.id === topMatch.id);
      if (component) {
        this.updateCache(normalizedRef, topMatch.id, topMatch.confidence, context?.pageId);
      }

      return {
        success: true,
        primaryMatch: this.createSemanticReference(referenceText, topMatch),
        allMatches: sortedMatches.slice(0, this.config.maxAlternatives),
        needsClarification: false,
        confidenceScores,
      };
    }

    // Low confidence - need clarification
    console.log(`[SemanticResolver] Low confidence (${topMatch.confidence.toFixed(2)}), requesting clarification`);
    return {
      success: false,
      primaryMatch: this.createSemanticReference(referenceText, topMatch),
      allMatches: sortedMatches.slice(0, this.config.maxAlternatives),
      needsClarification: true,
      clarificationOptions: this.createClarificationOptions(sortedMatches.slice(0, this.config.maxAlternatives)),
      confidenceScores,
    };
  }

  // ============================================================================
  // Matching Strategies
  // ============================================================================

  /**
   * Match by semantic role (_semanticRole field)
   */
  private matchBySemanticRole(reference: string, components: Component[]): ComponentMatch[] {
    const matches: ComponentMatch[] = [];
    const words = reference.split(/\s+/);

    for (const component of components) {
      const props: BaseComponentProperties = component.properties;
      const semanticRole = (props._semanticRole ?? '').toLowerCase();

      if (!semanticRole) continue;

      // Check direct role match
      if (semanticRole === reference || semanticRole.includes(reference)) {
        matches.push({
          id: component.id,
          name: component.name || component.type,
          type: component.type,
          matchReason: `Semantic role: "${semanticRole}"`,
          confidence: semanticRole === reference ? 0.95 : 0.88,
          semanticRole,
          position: this.getComponentPosition(component),
        });
        continue;
      }

      // Check word overlap with role
      const roleWords = semanticRole.split(/[-_\s]+/);
      const overlapCount = words.filter(w => roleWords.some((rw: string) => rw.includes(w) || w.includes(rw))).length;
      if (overlapCount > 0) {
        const confidence = 0.7 + (overlapCount / Math.max(words.length, roleWords.length)) * 0.2;
        matches.push({
          id: component.id,
          name: component.name || component.type,
          type: component.type,
          matchReason: `Semantic role partial match: "${semanticRole}"`,
          confidence,
          semanticRole,
          position: this.getComponentPosition(component),
        });
      }
    }

    return matches;
  }

  /**
   * Match by vocabulary aliases (Task 2.1.2)
   */
  private matchByVocabularyAlias(reference: string, components: Component[]): ComponentMatch[] {
    const matches: ComponentMatch[] = [];
    const words = reference.split(/\s+/);

    // Find which vocabulary entries match the reference
    const matchedVocab: string[] = [];
    for (const [canonical, aliases] of Object.entries(REFERENCE_VOCABULARY)) {
      if (aliases.some(alias => reference.includes(alias) || words.includes(alias))) {
        matchedVocab.push(canonical);
      }
    }

    if (matchedVocab.length === 0) return matches;

    console.log(`[VocabularyAlias] Matched vocabulary: ${matchedVocab.join(', ')}`);

    // Now find components that match the vocabulary
    for (const component of components) {
      const id = component.id.toLowerCase();
      const name = (component.name || '').toLowerCase();
      const type = component.type.toLowerCase();
      const props: BaseComponentProperties = component.properties;
      const semanticRole = (props._semanticRole ?? '').toLowerCase();
      const content = getFirstLowercaseString(props, 'content', 'text', 'label');

      for (const vocab of matchedVocab) {
        const aliases = REFERENCE_VOCABULARY[vocab];
        if (!aliases) continue;
        const allTerms = [vocab, ...aliases];

        // Check if component matches any of the vocabulary terms (including ID)
        for (const term of allTerms) {
          const idMatch = id.includes(term);
          const nameMatch = name.includes(term);
          const typeMatch = type.includes(term);
          const roleMatch = semanticRole.includes(term);
          const contentMatch = content.includes(term);

          if (idMatch || nameMatch || typeMatch || roleMatch || contentMatch) {
            // Confidence: name > id > semanticRole > type > content
            const baseConfidence = nameMatch ? 0.85 : idMatch ? 0.82 : roleMatch ? 0.80 : typeMatch ? 0.75 : 0.7;
            matches.push({
              id: component.id,
              name: component.name || component.type,
              type: component.type,
              matchReason: `Vocabulary match: "${vocab}" (via "${term}")`,
              confidence: baseConfidence,
              semanticRole: props?._semanticRole,
              position: this.getComponentPosition(component),
            });
            break; // Only add once per vocab entry
          }
        }
      }
    }

    return matches;
  }

  /**
   * Fuzzy matching using Levenshtein distance (Task 2.1.1)
   */
  private matchByFuzzyName(reference: string, components: Component[]): ComponentMatch[] {
    const matches: ComponentMatch[] = [];

    for (const component of components) {
      const id = component.id.toLowerCase();
      const name = (component.name || '').toLowerCase();

      // Calculate similarity for ID (often contains meaningful names like "dashboard-title")
      const idSimilarity = this.calculateSimilarity(reference, id);

      // Calculate similarity for name
      const nameSimilarity = name ? this.calculateSimilarity(reference, name) : 0;

      // Also check type similarity
      const typeSimilarity = this.calculateSimilarity(reference, component.type.toLowerCase());

      // Check content/label for text components
      const props: BaseComponentProperties = component.properties;
      const content = getFirstLowercaseString(props, 'content', 'text', 'label');
      const contentSimilarity = content ? this.calculateSimilarity(reference, content) : 0;

      // Weight: name > id > type > content
      const maxSimilarity = Math.max(
        nameSimilarity,
        idSimilarity * 0.9,
        typeSimilarity * 0.7,
        contentSimilarity * 0.6
      );

      if (maxSimilarity > 0.5) {
        let matchReason: string;
        if (nameSimilarity === maxSimilarity) {
          matchReason = `Fuzzy name match (${(nameSimilarity * 100).toFixed(0)}% similar)`;
        } else if (idSimilarity * 0.9 === maxSimilarity) {
          matchReason = `Fuzzy ID match (${(idSimilarity * 100).toFixed(0)}% similar)`;
        } else if (typeSimilarity * 0.7 === maxSimilarity) {
          matchReason = `Fuzzy type match`;
        } else {
          matchReason = `Fuzzy content match`;
        }

        matches.push({
          id: component.id,
          name: component.name || component.type,
          type: component.type,
          matchReason,
          confidence: maxSimilarity,
          semanticRole: props?._semanticRole,
          position: this.getComponentPosition(component),
        });
      }
    }

    return matches;
  }

  /**
   * Positional reference matching (Task 2.1.4)
   * Handles: "above", "below", "first", "last", "left of", "right of"
   */
  private matchByPosition(
    reference: string,
    components: Component[],
    context?: { selectedComponents?: Component[] }
  ): ComponentMatch[] {
    const matches: ComponentMatch[] = [];
    const words = reference.split(/\s+/);

    // Check for positional keywords
    let positionalMatch: typeof POSITIONAL_KEYWORDS[keyof typeof POSITIONAL_KEYWORDS] | null = null;
    let positionKeyword = '';

    for (const [keyword, config] of Object.entries(POSITIONAL_KEYWORDS)) {
      if (words.includes(keyword) || reference.includes(keyword)) {
        positionalMatch = config;
        positionKeyword = keyword;
        break;
      }
    }

    if (!positionalMatch) return matches;

    console.log(`[Positional] Detected positional reference: "${positionKeyword}"`);

    // Extract the component type being referenced (e.g., "the button above")
    const typeWords = words.filter(w => !Object.keys(POSITIONAL_KEYWORDS).includes(w) && !['the', 'a', 'an', 'component'].includes(w));
    const typeReference = typeWords.join(' ');

    // Get reference point
    let referenceComponent: Component | null = null;
    if (context?.selectedComponents && context.selectedComponents.length > 0) {
      referenceComponent = context.selectedComponents[0];
    }

    if ('index' in positionalMatch) {
      // Handle index-based references (first, second, third, last)
      let sortedComponents = [...components];

      // If we have a type filter, apply it
      if (typeReference) {
        sortedComponents = sortedComponents.filter(c => {
          const name = (c.name || '').toLowerCase();
          const type = c.type.toLowerCase();
          return name.includes(typeReference) || type.includes(typeReference);
        });
      }

      // Sort by position/order
      sortedComponents.sort((a, b) => {
        if (a.order !== b.order) return a.order - b.order;
        const posA = this.getComponentPosition(a);
        const posB = this.getComponentPosition(b);
        if (posA.y !== posB.y) return posA.y - posB.y;
        return posA.x - posB.x;
      });

      const targetIndex = positionalMatch.index === -1
        ? sortedComponents.length - 1
        : positionalMatch.index;

      if (targetIndex >= 0 && targetIndex < sortedComponents.length) {
        const target = sortedComponents[targetIndex];
        matches.push({
          id: target.id,
          name: target.name || target.type,
          type: target.type,
          matchReason: `${positionKeyword.charAt(0).toUpperCase() + positionKeyword.slice(1)} component${typeReference ? ` of type "${typeReference}"` : ''}`,
          confidence: 0.85,
          position: this.getComponentPosition(target),
        });
      }
    } else if (referenceComponent && 'direction' in positionalMatch && 'comparator' in positionalMatch) {
      // Handle relative positional references (above, below, left, right)
      const { direction, comparator } = positionalMatch;

      let candidates = typeReference
        ? components.filter(c => {
            const name = (c.name || '').toLowerCase();
            const type = c.type.toLowerCase();
            return name.includes(typeReference) || type.includes(typeReference) ||
                   this.matchesVocabulary(typeReference, name) || this.matchesVocabulary(typeReference, type);
          })
        : components.filter(c => c.id !== referenceComponent!.id);

      // Filter by position
      const refPos = this.getComponentPosition(referenceComponent);
      if (direction === 'horizontal') {
        candidates = candidates.filter(c => {
          const pos = this.getComponentPosition(c);
          return comparator === 'less' ? pos.x < refPos.x : pos.x > refPos.x;
        });
        // Sort by horizontal proximity
        candidates.sort((a, b) => {
          const posA = this.getComponentPosition(a);
          const posB = this.getComponentPosition(b);
          return Math.abs(posA.x - refPos.x) - Math.abs(posB.x - refPos.x);
        });
      } else if (direction === 'vertical') {
        candidates = candidates.filter(c => {
          const pos = this.getComponentPosition(c);
          return comparator === 'less' ? pos.y < refPos.y : pos.y > refPos.y;
        });
        // Sort by vertical proximity
        candidates.sort((a, b) => {
          const posA = this.getComponentPosition(a);
          const posB = this.getComponentPosition(b);
          return Math.abs(posA.y - refPos.y) - Math.abs(posB.y - refPos.y);
        });
      }

      // Take the closest match
      if (candidates.length > 0) {
        const closest = candidates[0];
        matches.push({
          id: closest.id,
          name: closest.name || closest.type,
          type: closest.type,
          matchReason: `Component ${positionKeyword} "${referenceComponent.name || referenceComponent.type}"`,
          confidence: 0.82,
          position: this.getComponentPosition(closest),
        });
      }
    }

    return matches;
  }

  /**
   * Match by contextual information (selected/recent components)
   */
  private matchByContext(
    reference: string,
    contextComponents: Component[],
    contextType: 'selected' | 'recent'
  ): ComponentMatch[] {
    const matches: ComponentMatch[] = [];
    const normalizedRef = reference.toLowerCase();

    // Check for context keywords
    const isContextReference =
      normalizedRef.includes('this') ||
      normalizedRef.includes('that') ||
      normalizedRef.includes('selected') ||
      normalizedRef.includes('current') ||
      (contextType === 'recent' && (normalizedRef.includes('new') || normalizedRef.includes('just added') || normalizedRef.includes('recent')));

    if (!isContextReference) {
      // Also try matching by name/type within context
      for (const component of contextComponents) {
        const name = (component.name || '').toLowerCase();
        const type = component.type.toLowerCase();

        if (name.includes(normalizedRef) || normalizedRef.includes(name) ||
            type.includes(normalizedRef) || normalizedRef.includes(type)) {
          matches.push({
            id: component.id,
            name: component.name || component.type,
            type: component.type,
            matchReason: `${contextType === 'selected' ? 'Selected' : 'Recently added'} component matching "${reference}"`,
            confidence: 0.8,
            position: this.getComponentPosition(component),
          });
        }
      }
      return matches;
    }

    // Direct context reference
    for (const component of contextComponents) {
      matches.push({
        id: component.id,
        name: component.name || component.type,
        type: component.type,
        matchReason: `${contextType === 'selected' ? 'Currently selected' : 'Recently added'} component`,
        confidence: 0.85,
        position: this.getComponentPosition(component),
      });
    }

    return matches;
  }

  // ============================================================================
  // Helper Methods
  // ============================================================================

  /**
   * Get position from component safely
   */
  private getComponentPosition(component: Component): { x: number; y: number } {
    return component.position || { x: 0, y: 0 };
  }

  /**
   * Check if a term matches any vocabulary entry
   */
  private matchesVocabulary(term: string, target: string): boolean {
    for (const [, aliases] of Object.entries(REFERENCE_VOCABULARY)) {
      if (aliases.includes(term) && aliases.some(a => target.includes(a))) {
        return true;
      }
    }
    return false;
  }

  /**
   * Normalize a reference for matching
   */
  private normalizeReference(reference: string): string {
    return reference
      .toLowerCase()
      .trim()
      .replace(/^(the|a|an)\s+/i, '') // Remove articles
      .replace(/\s+(component|element|widget|section)$/i, '') // Remove generic suffixes
      .replace(/['"]/g, '') // Remove quotes
      .replace(/\s+/g, ' '); // Normalize whitespace
  }

  /**
   * Calculate string similarity score using Levenshtein distance
   */
  private calculateSimilarity(str1: string, str2: string): number {
    if (!str1 || !str2) return 0;
    if (str1 === str2) return 1;

    // Check for substring match first
    if (str1.includes(str2) || str2.includes(str1)) {
      const shorter = str1.length < str2.length ? str1 : str2;
      const longer = str1.length >= str2.length ? str1 : str2;
      return 0.6 + (shorter.length / longer.length) * 0.4;
    }

    // Use Levenshtein distance
    const distance = this.levenshteinDistance(str1, str2);
    const maxLength = Math.max(str1.length, str2.length);

    // If distance is within threshold, calculate similarity
    if (distance <= this.config.maxFuzzyDistance) {
      return (maxLength - distance) / maxLength;
    }

    return (maxLength - distance) / maxLength;
  }

  /**
   * Calculate Levenshtein distance between two strings
   */
  private levenshteinDistance(str1: string, str2: string): number {
    const matrix: number[][] = [];

    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i];
    }

    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j;
    }

    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }

    return matrix[str2.length][str1.length];
  }

  /**
   * Deduplicate matches by component ID, keeping highest confidence
   */
  private deduplicateMatches(matches: ComponentMatch[]): ComponentMatch[] {
    const byId = new Map<string, ComponentMatch>();

    for (const match of matches) {
      const existing = byId.get(match.id);
      if (!existing || match.confidence > existing.confidence) {
        byId.set(match.id, match);
      }
    }

    return Array.from(byId.values());
  }

  /**
   * Create a semantic reference object
   */
  private createSemanticReference(referenceText: string, match: ComponentMatch): SemanticReference {
    // Infer match type from reason
    let matchType: SemanticMatchType = 'fuzzy';
    if (match.matchReason.includes('Semantic role')) matchType = 'semantic';
    else if (match.matchReason.includes('Vocabulary')) matchType = 'alias';
    else if (match.matchReason.includes('Exact')) matchType = 'exact';
    else if (match.matchReason.includes('above') || match.matchReason.includes('below') ||
             match.matchReason.includes('first') || match.matchReason.includes('last')) matchType = 'positional';
    else if (match.matchReason.includes('selected') || match.matchReason.includes('recent')) matchType = 'contextual';

    return {
      referenceText,
      resolvedComponentId: match.id,
      confidence: match.confidence,
      matchType,
      resolutionPath: match.matchReason,
    };
  }

  /**
   * Create a success result for single match
   */
  private createSuccessResult(
    referenceText: string,
    component: Component,
    confidence: number,
    matchType: SemanticMatchType,
    reason: string
  ): ReferenceResolutionResult {
    const props: BaseComponentProperties = component.properties;
    const match: ComponentMatch = {
      id: component.id,
      name: component.name || component.type,
      type: component.type,
      matchReason: reason,
      confidence,
      semanticRole: props._semanticRole,
      position: this.getComponentPosition(component),
    };

    return {
      success: true,
      primaryMatch: {
        referenceText,
        resolvedComponentId: component.id,
        confidence,
        matchType,
        resolutionPath: reason,
      },
      allMatches: [match],
      needsClarification: false,
    };
  }

  /**
   * Create clarification options from matches
   */
  private createClarificationOptions(matches: ComponentMatch[]): ClarificationOption[] {
    return matches.map((match, index) => ({
      id: match.id,
      // Include component type in label for clarity (e.g., "dashboard-title (text)" vs "header-row (flex-container)")
      label: `${match.name} (${match.type})`,
      description: match.matchReason,
      isRecommended: index === 0,
      metadata: {
        componentId: match.id,
        confidence: match.confidence,
        type: match.type,
        semanticRole: match.semanticRole,
      },
    }));
  }

  // ============================================================================
  // Caching (Task 2.1.6)
  // ============================================================================

  /**
   * Check cache for a reference
   */
  private checkCache(reference: string, pageId?: string): ReferenceCacheEntry | null {
    const key = this.getCacheKey(reference, pageId);
    const entry = this.cache.get(key);

    if (entry && Date.now() - entry.cachedAt < this.config.cacheTTL) {
      return entry;
    }

    // Entry expired or not found
    if (entry) {
      this.cache.delete(key);
    }

    return null;
  }

  /**
   * Update cache with a resolution
   */
  private updateCache(reference: string, componentId: string, confidence: number, pageId?: string): void {
    const key = this.getCacheKey(reference, pageId);
    this.cache.set(key, {
      reference,
      componentId,
      confidence,
      cachedAt: Date.now(),
      pageId,
    });

    console.log(`[Cache] Stored: "${reference}" -> ${componentId}`);
  }

  /**
   * Generate cache key
   */
  private getCacheKey(reference: string, pageId?: string): string {
    return `${pageId || 'global'}:${reference}`;
  }

  /**
   * Invalidate cache entries for a component
   */
  invalidateCacheForComponent(componentId: string): void {
    const keysToDelete: string[] = [];
    for (const [key, entry] of this.cache.entries()) {
      if (entry.componentId === componentId) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach(key => this.cache.delete(key));
    console.log(`[Cache] Invalidated ${keysToDelete.length} entries for component ${componentId}`);
  }

  /**
   * Invalidate all cache entries for a page
   */
  invalidateCacheForPage(pageId: string): void {
    const keysToDelete: string[] = [];
    for (const key of this.cache.keys()) {
      if (key.startsWith(`${pageId}:`)) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach(key => this.cache.delete(key));
    console.log(`[Cache] Invalidated ${keysToDelete.length} entries for page ${pageId}`);
  }

  /**
   * Clear all cache entries
   */
  clearCache(): void {
    const size = this.cache.size;
    this.cache.clear();
    console.log(`[Cache] Cleared all ${size} entries`);
  }

  // ============================================================================
  // Legacy Interface Methods (backward compatibility)
  // ============================================================================

  /**
   * Legacy resolve method for backward compatibility
   */
  resolveReference(reference: string, context?: {
    selectedComponents?: Component[];
    recentlyAddedComponents?: Component[];
    templateInstanceId?: string;
  }): ResolvedReference | null {
    const normalizedRef = reference.toLowerCase().trim();
    const { components } = useEditorStore.getState();

    // 1. Check for direct ID reference
    const directMatch = components.find(c => c.id === reference);
    if (directMatch) {
      return {
        componentId: directMatch.id,
        component: directMatch,
        confidence: 1.0,
        matchType: 'exact'
      };
    }

    // 2. Check for exact name match
    const exactNameMatch = components.find(c =>
      c.name?.toLowerCase() === normalizedRef
    );
    if (exactNameMatch) {
      return {
        componentId: exactNameMatch.id,
        component: exactNameMatch,
        confidence: 0.95,
        matchType: 'exact'
      };
    }

    // 3. Check template semantic references
    if (context?.templateInstanceId) {
      const templateComponent = templateInstantiationService.getComponentBySemanticReference(
        normalizedRef,
        context.templateInstanceId
      );
      if (templateComponent) {
        return {
          componentId: templateComponent.id,
          component: templateComponent,
          confidence: 0.9,
          matchType: 'semantic',
          semanticRole: normalizedRef
        };
      }
    }

    // 4. Check semantic mappings
    for (const [role, aliases] of Object.entries(this.semanticMappings)) {
      if (aliases.some(alias => normalizedRef.includes(alias))) {
        const semanticMatch = components.find(c => {
          const props: BaseComponentProperties = c.properties;
          const semanticRole = props._semanticRole?.toLowerCase();
          const componentName = c.name?.toLowerCase();

          return aliases.some(alias =>
            semanticRole?.includes(alias) ||
            componentName?.includes(alias)
          );
        });

        if (semanticMatch) {
          return {
            componentId: semanticMatch.id,
            component: semanticMatch,
            confidence: 0.85,
            matchType: 'semantic',
            semanticRole: role
          };
        }
      }
    }

    // 5. Check contextual references
    if (context?.selectedComponents && context.selectedComponents.length > 0) {
      const selectedMatch = context.selectedComponents.find(c => {
        const componentName = c.name?.toLowerCase() || '';
        const componentType = c.type.toLowerCase();

        return componentName.includes(normalizedRef) ||
               componentType.includes(normalizedRef) ||
               normalizedRef.includes('this') ||
               normalizedRef.includes('selected') ||
               normalizedRef.includes('current');
      });

      if (selectedMatch) {
        return {
          componentId: selectedMatch.id,
          component: selectedMatch,
          confidence: 0.8,
          matchType: 'contextual'
        };
      }
    }

    // 6. Check positional references
    const positionalMatch = this.resolvePositionalReferenceLegacy(normalizedRef, components);
    if (positionalMatch) {
      return positionalMatch;
    }

    // 7. Partial name match
    const partialMatch = components.find(c => {
      const componentName = c.name?.toLowerCase() || '';
      return componentName.includes(normalizedRef) || normalizedRef.includes(componentName);
    });

    if (partialMatch) {
      return {
        componentId: partialMatch.id,
        component: partialMatch,
        confidence: 0.6,
        matchType: 'contextual'
      };
    }

    // 8. Check recently added components
    if (context?.recentlyAddedComponents && context.recentlyAddedComponents.length > 0) {
      const recentMatch = context.recentlyAddedComponents.find(c => {
        const componentType = c.type.toLowerCase();
        return normalizedRef.includes('new') ||
               normalizedRef.includes('just added') ||
               normalizedRef.includes('recent') ||
               componentType.includes(normalizedRef.replace(/the |a |an /g, ''));
      });

      if (recentMatch) {
        return {
          componentId: recentMatch.id,
          component: recentMatch,
          confidence: 0.7,
          matchType: 'contextual'
        };
      }
    }

    return null;
  }

  /**
   * Legacy positional reference resolver
   */
  private resolvePositionalReferenceLegacy(reference: string, components: Component[]): ResolvedReference | null {
    const normalizedRef = reference.toLowerCase();

    const positions = {
      left: (c: Component) => this.getComponentPosition(c).x < 300,
      right: (c: Component) => this.getComponentPosition(c).x > 800,
      top: (c: Component) => this.getComponentPosition(c).y < 200,
      bottom: (c: Component) => this.getComponentPosition(c).y > 500,
      center: (c: Component) => {
        const pos = this.getComponentPosition(c);
        return pos.x > 400 && pos.x < 800;
      }
    };

    for (const [position, filter] of Object.entries(positions)) {
      if (normalizedRef.includes(position)) {
        const matches = components.filter(filter);

        if (matches.length === 1) {
          return {
            componentId: matches[0].id,
            component: matches[0],
            confidence: 0.75,
            matchType: 'positional'
          };
        } else if (matches.length > 1) {
          const sorted = matches.sort((a, b) =>
            new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
          );
          return {
            componentId: sorted[0].id,
            component: sorted[0],
            confidence: 0.6,
            matchType: 'positional'
          };
        }
      }
    }

    return null;
  }

  /**
   * Extract component references from a command
   * Filters out action/modifier words to get clean component references
   */
  extractComponentReferences(command: string): string[] {
    const references: string[] = [];
    const normalizedCommand = command.toLowerCase();

    // Skip extraction for breakpoint-specific commands - they don't reference components
    const breakpointPrefixPatterns = [
      /^on\s+(mobile|tablet|desktop)\s+/i,
      /^(mobile|tablet|desktop):\s*/i,
      /^only\s+on\s+(mobile|tablet|desktop)\s+/i,
      /^(mobile|tablet|desktop)\s+(bold|italic|underline|center|left|right|justify|h[1-6])/i,
      /^(?:remove|clear|no)\s+\w+\s+(?:from|on|for)\s+(mobile|tablet|desktop)/i,
    ];
    if (breakpointPrefixPatterns.some(pattern => pattern.test(normalizedCommand))) {
      console.log(`[extractComponentReferences] Skipping breakpoint command: "${command}"`);
      return [];
    }

    // Action/modifier words that should NOT be part of component references
    const actionWords = new Set([
      // Size modifiers
      'smaller', 'bigger', 'larger', 'smaller', 'tiny', 'huge', 'tall', 'short', 'wide', 'narrow',
      // Color modifiers and color-related words
      'red', 'blue', 'green', 'yellow', 'orange', 'purple', 'pink', 'black', 'white', 'gray', 'grey',
      'color', 'colour', 'background', 'foreground',
      // Style modifiers
      'bold', 'italic', 'underline', 'primary', 'secondary', 'tertiary', 'outline', 'ghost', 'solid',
      // Content/property words (not component names)
      'text', 'content', 'value', 'label', 'style', 'font', 'size', 'width', 'height',
      // Visibility modifiers
      'visible', 'hidden', 'transparent', 'opaque',
      // Position modifiers
      'centered', 'aligned', 'left', 'right', 'top', 'bottom',
      // Generic actions/pronouns
      'it', 'them', 'all', 'more', 'less', 'to', 'a', 'an', 'the',
      // Breakpoint words (should not be treated as component references)
      'mobile', 'tablet', 'desktop',
      // Gauge/chart property words (not component names)
      'unit', 'units', 'min', 'max', 'minimum', 'maximum', 'range', 'scale',
      // Measurement units (not component names)
      'feet', 'meters', 'percent', 'celsius', 'fahrenheit', 'psi', 'bar', 'kpa',
      // Temporal words (data query terms, not component names)
      'current', 'latest', 'recent', 'last', 'historical', 'previous',
      // Data query words (not component names)
      'reading', 'readings', 'measurement', 'measurements',
    ]);

    const patterns = [
      /(?:the|this|that)\s+(\w+(?:\s+\w+)?)/g,
      /(?:make|change|update|modify)\s+(?:the\s+)?(\w+(?:\s+\w+)?)/g,
      /(\w+(?:\s+\w+)?)\s+(?:component|element|section|area|panel)/g,
    ];

    patterns.forEach(pattern => {
      let match;
      while ((match = pattern.exec(normalizedCommand)) !== null) {
        if (match[1]) {
          // Filter out action words from the captured reference
          const words = match[1].split(/\s+/);
          const filteredWords = words.filter(w => !actionWords.has(w));

          if (filteredWords.length > 0) {
            const cleanRef = filteredWords.join(' ');
            if (!actionWords.has(cleanRef)) {
              references.push(cleanRef);
            }
          }
        }
      }
    });

    // Check for vocabulary keywords (but skip action verbs and property words that aren't component references)
    const actionVerbs = new Set(['change', 'update', 'modify', 'make', 'set', 'edit', 'add', 'remove', 'delete']);
    for (const [canonical, aliases] of Object.entries(REFERENCE_VOCABULARY)) {
      // Skip if the canonical term is an action verb (e.g., "edit" vocabulary triggered by "change")
      if (actionVerbs.has(canonical)) continue;

      for (const alias of aliases) {
        // Only match if the alias isn't an action verb/property word and appears as a whole word
        // Use word boundary regex to prevent matching "table" inside "tablet"
        if (!actionVerbs.has(alias) && !actionWords.has(alias)) {
          const wordBoundaryRegex = new RegExp(`\\b${alias}\\b`, 'i');
          if (wordBoundaryRegex.test(normalizedCommand)) {
            references.push(canonical);
            break;
          }
        }
      }
    }

    const uniqueRefs = [...new Set(references)];
    console.log(`[extractComponentReferences] Command: "${command}" -> References: [${uniqueRefs.join(', ')}]`);
    return uniqueRefs;
  }

  /**
   * Get suggestions for ambiguous references
   */
  getSuggestions(reference: string, components: Component[]): Component[] {
    const normalizedRef = reference.toLowerCase();
    const suggestions: Component[] = [];

    components.forEach(component => {
      const name = component.name?.toLowerCase() || '';
      const type = component.type.toLowerCase();
      const props: BaseComponentProperties = component.properties;
      const semanticRole = props._semanticRole?.toLowerCase() || '';

      const score = this.calculateSimilarity(normalizedRef, name) +
                   this.calculateSimilarity(normalizedRef, type) * 0.5 +
                   this.calculateSimilarity(normalizedRef, semanticRole) * 0.8;

      if (score > 0.3) {
        suggestions.push(component);
      }
    });

    return suggestions.sort((a, b) => {
      const scoreA = this.calculateSimilarity(normalizedRef, a.name?.toLowerCase() || '');
      const scoreB = this.calculateSimilarity(normalizedRef, b.name?.toLowerCase() || '');
      return scoreB - scoreA;
    }).slice(0, 5);
  }
}

export const componentReferenceResolver = ComponentReferenceResolver.getInstance();
